import { useCallback, useEffect, useState } from 'react';
import { access, type Role } from '../api/access';
import { chart, type Profession, type SaveProfession } from '../api/chart';
import {
  IconBriefcase,
  IconCircleAlert,
  IconPencil,
  IconPlus,
  IconRefreshCw,
  IconTrash2,
} from '../icons';
import { CODE_MAX, fold } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, type Column } from '../ui/Table';

/**
 * Trades: what somebody is, as against the seat they sit in.
 *
 * What is edited here is what everybody of a trade may do, wherever they sit. Written once
 * instead of on two hundred seats — which is both less work and the only version of it that
 * does not drift.
 */

/** A code suggested from the name, folded the same way the search box folds. */
function suggestCode(name: string): string {
  return fold(name)
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

const blank: SaveProfession = {
  code: '',
  name: '',
  description: null,
  isActive: true,
  roleIds: [],
};

export function Professions() {
  const t = useTranslate();
  const [rows, setRows] = useState<Profession[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [draft, setDraft] = useState<{ id: string | null; values: SaveProfession } | null>(null);
  const [doomed, setDoomed] = useState<Profession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [codeFollows, setCodeFollows] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, allRoles] = await Promise.all([chart.professions.list(), access.roles.list()]);
      setRows(list);
      setRoles(allRoles.filter((r) => r.isActive));
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const values = draft?.values;
  const set = (change: Partial<SaveProfession>) =>
    setDraft((d) => (d ? { ...d, values: { ...d.values, ...change } } : d));

  /** Works from the previous draft: two boxes ticked in one render must not lose the first. */
  function toggleRole(roleId: string) {
    setDraft((previous) => {
      if (!previous) return previous;

      const held = previous.values.roleIds.includes(roleId);

      return {
        ...previous,
        values: {
          ...previous.values,
          roleIds: held
            ? previous.values.roleIds.filter((r) => r !== roleId)
            : [...previous.values.roleIds, roleId],
        },
      };
    });
  }

  async function save() {
    if (!draft || busy) return;

    setBusy(true);
    try {
      const body: SaveProfession = {
        ...draft.values,
        code: draft.values.code.trim(),
        name: draft.values.name.trim(),
        description: draft.values.description?.trim() || null,
      };

      if (draft.id) await chart.professions.update(draft.id, body);
      else await chart.professions.create(body);

      setDraft(null);
      setPanelError(null);
      await reload();
    } catch (e) {
      setPanelError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: Profession) {
    setBusy(true);
    try {
      await chart.professions.remove(row.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  const canSave = Boolean(values && values.name.trim() && (draft?.id || values.code.trim()));

  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? id;

  const columns: Column<Profession>[] = [
    {
      id: 'name',
      header: t('common.name'),
      className: 'is-primary',
      cell: (row) => (
        <>
          {row.name}
          {row.description ? <div className="nv-hint">{row.description}</div> : null}
        </>
      ),
      skeletonWidth: '48%',
    },
    {
      id: 'code',
      header: t('common.code'),
      width: '1%',
      cell: (row) => <span className="nv-code">{row.code}</span>,
      skeletonWidth: '72px',
    },
    {
      id: 'roles',
      header: t('professions.grants'),
      cell: (row) =>
        row.roleIds.length === 0 ? (
          <span className="nv-subtle">{t('common.none')}</span>
        ) : (
          <span className="nv-badge-group">
            {row.roleIds.map((id) => (
              <Badge key={id} tone="neutral" shape="square">{roleName(id)}</Badge>
            ))}
          </span>
        ),
      skeletonWidth: '70%',
    },
    {
      id: 'positions',
      header: t('professions.seats'),
      align: 'right',
      cell: (row) =>
        row.positionCount > 0 ? row.positionCount : <span className="nv-subtle">{t('common.none')}</span>,
      skeletonWidth: '28px',
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (row) => (
        <Badge tone={row.isActive ? 'comment' : 'neutral'}>
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
      skeletonWidth: '62px',
    },
  ];

  function open(row: Profession) {
    setPanelError(null);
    setCodeFollows(false);
    setDraft({
      id: row.id,
      values: {
        code: row.code,
        name: row.name,
        description: row.description,
        isActive: row.isActive,
        roleIds: row.roleIds,
      },
    });
  }

  function openNew() {
    setPanelError(null);
    setCodeFollows(true);
    setDraft({ id: null, values: { ...blank } });
  }

  return (
    <>
      <PageHeader
        crumbs={[t('app.people'), t('nav.professions')]}
        title={t('professions.title')}
        subtitle={t('professions.subtitle')}
        action={
          <Button variant="primary" icon={IconPlus} onClick={openNew}>{t('professions.add')}</Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <Table
          caption={t('professions.title')}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={open}
          rowActions={(row) => {
            const blocked =
              row.positionCount > 0 ? t('professions.seatedCannotDelete', { count: row.positionCount }) : null;

            return (
              <>
                <IconButton label={t('common.edit')} icon={IconPencil} onClick={() => open(row)} />
                <RowMenu
                  label={t('common.moreActions')}
                  items={[
                    {
                      label: blocked ?? t('common.delete'),
                      icon: IconTrash2,
                      danger: true,
                      disabled: busy || blocked !== null,
                      onSelect: () => setDoomed(row),
                    },
                  ]}
                />
              </>
            );
          }}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconBriefcase}
              title={t('professions.empty')}
              text={t('professions.emptyText')}
              action={
                <Button variant="primary" icon={IconPlus} onClick={openNew}>{t('professions.add')}</Button>
              }
            />
          }
        />
      </Card>

      <Drawer
        open={draft !== null}
        title={t(draft?.id ? 'professions.editTitle' : 'professions.newTitle')}
        onClose={() => setDraft(null)}
        footer={
          <>
            <Button onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!canSave || busy}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() => void save()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && values ? (
          <>
            <Field label={t('common.name')} required>
              {({ id }) => (
                <TextInput
                  id={id}
                  placeholder="Mağaza Müdürü"
                  value={values.name}
                  onChange={(e) =>
                    set({
                      name: e.target.value,
                      ...(codeFollows ? { code: suggestCode(e.target.value) } : {}),
                    })
                  }
                />
              )}
            </Field>

            <Field label={t('common.code')} hint={t('professions.codeHint')}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  maxLength={CODE_MAX}
                  value={values.code}
                  onChange={(e) => {
                    setCodeFollows(false);
                    set({ code: e.target.value });
                  }}
                />
              )}
            </Field>

            <Field label={t('common.description')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  placeholder={t('common.optional')}
                  value={values.description ?? ''}
                  onChange={(e) => set({ description: e.target.value || null })}
                />
              )}
            </Field>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('professions.grants')}</h3>
              <p className="nv-hint">{t('professions.grantsHint')}</p>

              <div className="nv-permission-list">
                {roles.map((role) => (
                  <label className="nv-permission" key={role.id}>
                    <input
                      type="checkbox"
                      checked={values.roleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    <span>
                      {role.name}
                      {role.description ? <span className="nv-hint">{role.description}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="nv-drawer__section">
              <Toggle
                checked={values.isActive}
                onCheckedChange={(isActive) => set({ isActive })}
                label={t('common.active')}
              />
              <p className="nv-hint">{t('professions.isActiveHint')}</p>
            </div>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: doomed.name }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? remove(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        <p>{t('common.deleteWarning')}</p>
      </ConfirmDialog>
    </>
  );
}
